import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';

import type { Provider } from '@vgtv/api-client';
import type { ProviderAccessMap } from '@vgtv/svp-access/lib/access_mappings';

import { access } from '../../services/svp/access';

export interface AccessContextProps {
  accesses: ProviderAccessMap[Provider][];
  isFetching: boolean;
  isFetched: boolean;
}

export const AccessContext = createContext<AccessContextProps>(undefined!);

export const AccessProvider: FC = ({ children }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [accesses, setAccesses] = useState<ProviderAccessMap[Provider][]>([]);

  useEffect(() => {
    access
      .getAccesses()
      .then((accesses) => {
        setAccesses(accesses);

        setIsLoaded(true);
      })
      .catch((err) => {
        console.error(err);

        setIsLoaded(true);
      });
  }, []);

  const state = useMemo(() => {
    return {
      accesses,
      isFetching: !isLoaded,
      isFetched: isLoaded,
    };
  }, [accesses, isLoaded]);

  return (
    <AccessContext.Provider value={state}>{children}</AccessContext.Provider>
  );
};

export const useAccessContext = () => {
  const access = useContext(AccessContext);

  if (process.env.NODE_ENV !== 'production' && !access) {
    throw new Error(
      'Access context is undefined, please verify you are calling useAccessContext() as child of a <AccessProvider> component.'
    );
  }

  return access;
};
